// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  src/features/financeiro/services/faturasService.ts
//
//  FATURA de contas a receber: documento de cobrança que agrupa parcelas já
//  existentes de um mesmo cliente numa data única. A dívida continua nos
//  títulos originais — por isso "baixar a fatura" baixa as parcelas agrupadas.
// ════════════════════════════════════════════════════════════════════════════

import { apiClient } from '@/lib/apiClient';
import type {
  ClienteFaturavelDto,
  FaturaResumoDto,
  FaturaDetalheDto,
  GerarFaturasRequest,
  GerarFaturasResultadoDto,
  BaixarFaturaRequest,
  FiltroFaturas,
  PaginadoResponse,
} from '../types';

const BASE = '/api/financeiro/faturas';

export interface FiltroCandidatas {
  clienteId?: number;
  grupoClienteId?: number;
  dtInicio?: string;
  dtFim?: string;
  busca?: string;
}

export const faturasService = {
  /** Parcelas em aberto ainda não faturadas, agrupadas por cliente. */
  async getCandidatas(filtro: FiltroCandidatas = {}): Promise<ClienteFaturavelDto[]> {
    const { data } = await apiClient.get<ClienteFaturavelDto[]>(`${BASE}/candidatas`, {
      params: {
        clienteId:      filtro.clienteId || undefined,
        grupoClienteId: filtro.grupoClienteId || undefined,
        dtInicio:       filtro.dtInicio || undefined,
        dtFim:          filtro.dtFim || undefined,
        busca:          filtro.busca && filtro.busca.trim() ? filtro.busca.trim() : undefined,
      },
    });
    return data;
  },

  /** Gera as faturas em lote — uma por cliente do request. */
  async gerar(req: GerarFaturasRequest): Promise<GerarFaturasResultadoDto> {
    const { data } = await apiClient.post<GerarFaturasResultadoDto>(BASE, req);
    return data;
  },

  async getAll(filtro: FiltroFaturas = {}): Promise<PaginadoResponse<FaturaResumoDto>> {
    const { data } = await apiClient.get<PaginadoResponse<FaturaResumoDto>>(BASE, {
      params: {
        pagina:        filtro.pagina ?? 1,
        tamanhoPagina: filtro.tamanhoPagina ?? 20,
        busca:         filtro.busca && filtro.busca.trim() ? filtro.busca.trim() : undefined,
        clienteId:     filtro.clienteId || undefined,
        dtInicio:      filtro.dtInicio || undefined,
        dtFim:         filtro.dtFim || undefined,
        situacao:      filtro.situacao && filtro.situacao !== 'todas' ? filtro.situacao : undefined,
        ordenarPor:    filtro.ordenarPor || undefined,
        decrescente:   filtro.ordenarPor ? filtro.decrescente ?? true : undefined,
      },
    });
    return data;
  },

  async getById(id: number): Promise<FaturaDetalheDto> {
    const { data } = await apiClient.get<FaturaDetalheDto>(`${BASE}/${id}`);
    return data;
  },

  /** Recebe a fatura — na prática, baixa as parcelas que ela agrupa. */
  async baixar(id: number, req: BaixarFaturaRequest): Promise<FaturaDetalheDto> {
    const { data } = await apiClient.put<FaturaDetalheDto>(`${BASE}/${id}/baixar`, req);
    return data;
  },

  /** Estorna: desfaz o agrupamento e cancela a fatura (as parcelas voltam). */
  async estornar(id: number): Promise<void> {
    await apiClient.delete(`${BASE}/${id}`);
  },

  /**
   * Baixa o PDF da fatura. Vem como blob autenticado — não dá para usar um
   * <a href> direto, porque o endpoint exige o header de Authorization.
   */
  async baixarPdf(id: number): Promise<void> {
    const { data } = await apiClient.get<Blob>(`${BASE}/${id}/pdf`, { responseType: 'blob' });

    const url = URL.createObjectURL(data);
    const a = document.createElement('a');
    a.href = url;
    a.download = `fatura-${id}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  },
};
